import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Button from './Button';
import FormField from './FormField';


interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
    totalItems?: number;
    itemsPerPage?: number;
    className?: string;
}

const options = [
    { label: '10', value: "10" },
    { label: '25', value: "25" },
    { label: '50', value: "50" },
    { label: '100', value: "100" },
];

const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    totalItems,
    itemsPerPage,
    className = '',
    onPageSizeChange,
}) => {
    if (totalPages <= 1 && !totalItems) return null;

    const renderPageNumbers = () => {
        const pages = [];
        const gap = <span key="gap" className="px-1 text-muted-foreground/50 select-none">...</span>;

        // Helper to create a page button
        const createPageButton = (pageNumber: number) => (
            <Button
                key={pageNumber}
                variant={currentPage === pageNumber ? 'primary' : 'outline'}
                size="sm"
                onClick={() => onPageChange(pageNumber)}
                className={`w-9 h-9 p-0 rounded-lg text-xs font-bold transition-all duration-200 ${currentPage === pageNumber
                    ? 'shadow-md shadow-primary/20 scale-110 z-10'
                    : 'hover:bg-primary/5 hover:border-primary/30'
                    }`}
            >
                {pageNumber}
            </Button>
        );

        if (totalPages <= 5) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(createPageButton(i));
            }
        } else {
            // Always show first page
            pages.push(createPageButton(1));

            if (currentPage > 3) {
                pages.push(gap);
            }

            // Show pages around current page
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);

            for (let i = start; i <= end; i++) {
                if (i !== 1 && i !== totalPages) {
                    pages.push(createPageButton(i));
                }
            }

            if (currentPage < totalPages - 2) {
                pages.push(gap);
            }

            // Always show last page
            pages.push(createPageButton(totalPages));
        }

        return pages;
    };

    const startItem = itemsPerPage ? (currentPage - 1) * itemsPerPage + 1 : 0;
    const endItem = itemsPerPage ? Math.min(currentPage * itemsPerPage, totalItems || 0) : 0;

    return (
        <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 py-4 ${className}`}>
            <div className="flex items-center gap-3">
                {totalPages > 1 && onPageSizeChange && (
                    <FormField
                        type="select"
                        value={String(itemsPerPage ?? '')}
                        options={options}
                        onValueChange={(e) => onPageSizeChange(Number(e))}
                        wrapperClass="mb-0"
                        inputClass="h-9 px-3 py-1.5 rounded-md border bg-background text-sm text-foreground w-[72px]"
                    />
                )}
                {totalItems !== undefined && itemsPerPage !== undefined && (
                    <div className="text-sm text-muted-foreground whitespace-nowrap">
                        Showing <span className="font-medium text-foreground">{startItem}</span> to{' '}
                        <span className="font-medium text-foreground">{endItem}</span> of{' '}
                        <span className="font-medium text-foreground">{totalItems}</span> results
                    </div>
                )}
            </div>

            <div className="flex items-center gap-1 ml-auto">
                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="h-9 w-9 rounded-xl border-secondary/20 hover:bg-primary/5 hover:border-primary/30 transition-all duration-200 shadow-sm"
                >
                    <ChevronLeft size={18} className={currentPage <= 1 ? "opacity-30" : "text-primary"} />
                </Button>

                <div className="flex items-center gap-1.5 mx-1">
                    {renderPageNumbers()}
                </div>

                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="h-9 w-9 rounded-xl border-secondary/20 hover:bg-primary/5 hover:border-primary/30 transition-all duration-200 shadow-sm"
                >
                    <ChevronRight size={18} className={currentPage >= totalPages ? "opacity-30" : "text-primary"} />
                </Button>
            </div>
        </div>
    );
};

export default Pagination;
